// Release smoke/regression flows — author + manage the DB-stored test flows
// run against deployed envs. Flows carry tags (smoke/regression preselect the
// run modal) + side-effect tags (drive the per-env policy). Also edits the
// per-app scripted-login config. The "Run release test" button opens the
// config modal.
import { useCallback, useMemo, useState } from "react";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import Shell from "./Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  RefreshButton,
  apiErrorMessage,
  relativeTime
} from "./shared";
import ReleaseTestModal from "./ReleaseTestModal";
import "./qa-flows.css";

const APP_LABEL = {
  portal: "Portal (PX)",
  applicant: "Applicant (AX)",
  crm: "CRM (LX)"
};
const COMMON_TAGS = ["smoke", "regression"];

const EMPTY_FLOW = {
  appKey: "portal",
  name: "",
  tags: [],
  sideEffects: [],
  preconditions: "",
  steps: "",
  expected: "",
  data: ""
};

// Free-form tag editor: removable chips + a text field that adds a tag on
// Enter/comma (and on blur). Dedupes case-insensitively but keeps the display
// casing as typed; `suggestions` drives native autocomplete from existing tags.
let tagInputSeq = 0;
function TagInput({ value, onChange, suggestions, placeholder }) {
  const tags = value || [];
  const [text, setText] = useState("");
  const [listId] = useState(() => {
    tagInputSeq += 1;
    return `qa-tags-${tagInputSeq}`;
  });

  const addTag = (raw) => {
    const t = String(raw || "").trim();
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setText("");
      return;
    }
    onChange([...tags, t]);
    setText("");
  };
  const removeTag = (t) => onChange(tags.filter((x) => x !== t));
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(text);
    } else if (e.key === "Backspace" && !text && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  };
  const remaining = (suggestions || []).filter(
    (s) => !tags.some((x) => x.toLowerCase() === s.toLowerCase())
  );

  return (
    <div className="qa-tag-input">
      {tags.map((t) => (
        <span key={t} className="qa-tag qa-tag-removable">
          {t}
          <button
            type="button"
            className="qa-tag-x"
            aria-label={`Remove ${t}`}
            onClick={() => removeTag(t)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="qa-tag-field"
        list={listId}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => addTag(text)}
        placeholder={placeholder || "Type a tag, Enter to add…"}
      />
      <datalist id={listId}>
        {remaining.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  );
}

function FlowEditor({ open, flow, apps, sideEffectTags, tagSuggestions, onClose, onSaved }) {
  const isEdit = !!(flow && flow.id);
  const [form, setForm] = useState(EMPTY_FLOW);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Seed the form when the modal opens for a specific flow.
  const [seededId, setSeededId] = useState(null);
  if (open && (flow ? flow.id : "new") !== seededId) {
    setSeededId(flow ? flow.id : "new");
    setForm(
      flow
        ? {
            appKey: flow.appKey,
            name: flow.name,
            tags: flow.tags || [],
            sideEffects: flow.sideEffects || [],
            preconditions: flow.preconditions || "",
            steps: Array.isArray(flow.steps) ? flow.steps.join("\n") : "",
            expected: flow.expected || "",
            data: flow.data || ""
          }
        : EMPTY_FLOW
    );
    setError(null);
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, v) =>
    setForm((f) => {
      const has = (f[k] || []).includes(v);
      return {
        ...f,
        [k]: has ? f[k].filter((x) => x !== v) : [...(f[k] || []), v]
      };
    });

  const save = async () => {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      appKey: form.appKey,
      name: form.name.trim(),
      tags: form.tags,
      sideEffects: form.sideEffects,
      preconditions: form.preconditions.trim() || null,
      // Split into one-step-per-line ONLY at save — editing keeps the raw text
      // so newlines + spaces survive while typing.
      steps: form.steps
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      expected: form.expected.trim() || null,
      data: form.data.trim() || null
    };
    try {
      if (isEdit) await api.bssQaFlowUpdate(flow.id, payload);
      else await api.bssQaFlowCreate(payload);
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      setError(apiErrorMessage(err) || "Could not save the flow");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit flow" : "New flow"}
      width={620}
      footer={
        <>
          <button type="button" className="bss-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            disabled={saving}
            onClick={save}
          >
            {saving ? "Saving…" : "Save flow"}
          </button>
        </>
      }
    >
      <div className="qa-form">
        <label className="qa-form-label">App</label>
        <select
          className="bss-input"
          value={form.appKey}
          onChange={(e) => set("appKey", e.target.value)}
        >
          {(apps || []).map((a) => (
            <option key={a} value={a}>
              {APP_LABEL[a] || a}
            </option>
          ))}
        </select>

        <label className="qa-form-label">Name</label>
        <input
          className="bss-input"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. Book a guided showing and confirm the email"
        />

        <label className="qa-form-label">Tags (group + preselect in the run modal)</label>
        <TagInput
          value={form.tags}
          onChange={(next) => set("tags", next)}
          suggestions={tagSuggestions}
        />

        <label className="qa-form-label">Side effects (drive env policy)</label>
        <div className="qa-form-tags">
          {(sideEffectTags || []).map((t) => (
            <label key={t} className="qa-check qa-check-warn">
              <input
                type="checkbox"
                checked={form.sideEffects.includes(t)}
                onChange={() => toggleIn("sideEffects", t)}
              />
              {t}
            </label>
          ))}
        </div>

        <label className="qa-form-label">Preconditions</label>
        <input
          className="bss-input"
          value={form.preconditions}
          onChange={(e) => set("preconditions", e.target.value)}
          placeholder="e.g. test partner P-TEST with a guided-showing-eligible lead"
        />

        <label className="qa-form-label">Steps (one per line — optional)</label>
        <textarea
          className="bss-input qa-form-textarea qa-textarea-steps"
          rows={7}
          value={form.steps}
          onChange={(e) => set("steps", e.target.value)}
          placeholder={
            "Open Settings → Showings\nBook a guided showing for the test lead\nCheck the activity timeline"
          }
        />

        <label className="qa-form-label">Expected result</label>
        <textarea
          className="bss-input qa-form-textarea qa-textarea-expected"
          rows={4}
          value={form.expected}
          onChange={(e) => set("expected", e.target.value)}
          placeholder="What a PASS looks like"
        />

        <label className="qa-form-label">Test data hints (optional)</label>
        <input
          className="bss-input"
          value={form.data}
          onChange={(e) => set("data", e.target.value)}
          placeholder="e.g. partner=P-TEST, lead=L-TEST"
        />

        {error && <p className="qa-form-error">{error}</p>}
      </div>
    </Modal>
  );
}

function LoginEditor({ open, app, login, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [seeded, setSeeded] = useState(null);

  if (open && app !== seeded) {
    setSeeded(app);
    setForm({
      loginPath: login?.loginPath || "/login",
      usernameSel: login?.usernameSel || "",
      passwordSel: login?.passwordSel || "",
      submitSel: login?.submitSel || "",
      successSel: login?.successSel || ""
    });
    setError(null);
  }
  if (!open || !form)
    return open ? <Modal open onClose={onClose} title="Login config" /> : null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.bssQaAppLoginUpsert(app, form);
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      setError(apiErrorMessage(err) || "Could not save the login config");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["loginPath", "Login path", "/login"],
    ["usernameSel", "Username selector", 'input[name="email"]'],
    ["passwordSel", "Password selector", 'input[type="password"]'],
    ["submitSel", "Submit selector", 'button[type="submit"]'],
    ["successSel", "Success selector (post-login)", "nav[aria-label='Primary']"]
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Login config — ${APP_LABEL[app] || app}`}
      width={560}
      footer={
        <>
          <button type="button" className="bss-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            disabled={saving}
            onClick={save}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="qa-form">
        <p className="qa-form-hint">
          The runner fills these selectors with the operator-supplied creds
          (unrecorded, never logged) before recording starts. Leave empty for a
          public/no-login app.
        </p>
        {fields.map(([k, label, ph]) => (
          <div key={k}>
            <label className="qa-form-label">{label}</label>
            <input
              className="bss-input"
              value={form[k]}
              onChange={(e) => set(k, e.target.value)}
              placeholder={ph}
            />
          </div>
        ))}
        {error && <p className="qa-form-error">{error}</p>}
      </div>
    </Modal>
  );
}

// A pending (IA-imported) flow: an inline editor to assign the app + tags +
// side-effects, then publish (requires an app) or discard (archive). The IA's
// auto-guessed app preselects when present; until an app is chosen Publish
// stays disabled (the server rejects a publish without a valid app anyway).
function PendingFlowRow({
  flow,
  apps,
  sideEffectTags,
  tagSuggestions,
  onPublished,
  onDiscarded
}) {
  const [appKey, setAppKey] = useState(flow.appKey || "");
  const [tags, setTags] = useState(flow.tags || []);
  const [sideEffects, setSideEffects] = useState(flow.sideEffects || []);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (list, setList, value) =>
    setList(
      list.includes(value) ? list.filter((x) => x !== value) : [...list, value]
    );

  const publish = async () => {
    if (!appKey) {
      setError("Choose an app before publishing");
      return;
    }
    setPublishing(true);
    setError(null);
    try {
      await api.bssQaFlowPublish(flow.id, { appKey, tags, sideEffects });
      onPublished && onPublished();
    } catch (err) {
      setError(apiErrorMessage(err) || "Could not publish the flow");
    } finally {
      setPublishing(false);
    }
  };

  const discard = async () => {
    if (
      !window.confirm(`Discard "${flow.name}"? It will be archived and hidden.`)
    )
      return;
    await api.bssQaFlowUpdate(flow.id, { archived: true });
    onDiscarded && onDiscarded();
  };

  return (
    <div className="qa-pending-row">
      <div className="qa-pending-hd">
        <span className="qa-pending-name">{flow.name}</span>
        {flow.sourceRef && (
          <span className="qa-pending-src">{flow.sourceRef}</span>
        )}
      </div>

      <div className="qa-pending-form">
        <div>
          <label className="qa-form-label">App</label>
          <select
            className="bss-input"
            value={appKey}
            onChange={(e) => setAppKey(e.target.value)}
          >
            <option value="">Select an app…</option>
            {(apps || []).map((a) => (
              <option key={a} value={a}>
                {APP_LABEL[a] || a}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="qa-form-label">Tags</label>
          <TagInput value={tags} onChange={setTags} suggestions={tagSuggestions} />
        </div>

        <div>
          <label className="qa-form-label">Side effects</label>
          <div className="qa-form-tags">
            {(sideEffectTags || []).map((t) => (
              <label key={t} className="qa-check qa-check-warn">
                <input
                  type="checkbox"
                  checked={sideEffects.includes(t)}
                  onChange={() => toggle(sideEffects, setSideEffects, t)}
                />
                {t}
              </label>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="qa-form-error">{error}</p>}

      <div className="qa-pending-actions">
        <button
          type="button"
          className="qa-link-btn qa-link-danger"
          onClick={discard}
        >
          Discard
        </button>
        <button
          type="button"
          className="bss-btn bss-btn--primary bss-btn--sm"
          disabled={!appKey || publishing}
          onClick={publish}
          title={
            appKey
              ? "Publish — makes this flow runnable"
              : "Choose an app first"
          }
        >
          <Icon.Rocket size={13} /> {publishing ? "Publishing…" : "Publish"}
        </button>
      </div>
    </div>
  );
}

const fetchAll = async () => {
  // Published + pending are fetched separately so the run modal only ever sees
  // runnable (published) flows, while the Pending tab surfaces IA imports that
  // still need an app before they can be published.
  const [published, pending, meta, logins] = await Promise.all([
    api.bssQaFlows({ includeArchived: false, status: "published" }),
    api.bssQaFlows({ includeArchived: false, status: "pending" }),
    api.bssReleaseMeta(),
    api.bssQaAppLogins()
  ]);
  return {
    published: published.data.flows || [],
    pending: pending.data.flows || [],
    meta: meta.data || {},
    logins: logins.data.logins || []
  };
};

export default function QaFlowsPage() {
  const { data, error, loading, refresh } = useBssPoll(fetchAll);
  const [editing, setEditing] = useState(null); // flow object, or {} for new, or null
  const [loginApp, setLoginApp] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const [tab, setTab] = useState("published"); // "published" | "pending"

  const published = data?.published || [];
  const pending = data?.pending || [];
  const meta = data?.meta || {};
  const logins = data?.logins || [];
  const apps = meta.apps || ["portal", "applicant", "crm"];
  const sideEffectTags = meta.sideEffectTags || [
    "mutating",
    "sends_comms",
    "charges"
  ];

  const loginByApp = useMemo(() => {
    const m = {};
    for (const l of logins) m[l.appKey] = l;
    return m;
  }, [logins]);

  // Published flows are grouped by app (a published flow always has an app).
  const flowsByApp = useMemo(() => {
    const m = new Map();
    for (const a of apps) m.set(a, []);
    for (const f of published) {
      const arr = m.get(f.appKey) || [];
      arr.push(f);
      m.set(f.appKey, arr);
    }
    return m;
  }, [published, apps]);

  // Existing tags across all flows (+ the smoke/regression conventions) drive
  // the tag-input autocomplete. Case-insensitively unique; first casing wins.
  const tagSuggestions = useMemo(() => {
    const seen = new Map();
    for (const t of COMMON_TAGS) seen.set(t.toLowerCase(), t);
    for (const f of [...published, ...pending]) {
      for (const t of f.tags || []) {
        const k = String(t).toLowerCase();
        if (!seen.has(k)) seen.set(k, t);
      }
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [published, pending]);

  const archive = useCallback(
    async (flow) => {
      if (
        !window.confirm(`Archive "${flow.name}"? It will be hidden from runs.`)
      )
        return;
      await api.bssQaFlowUpdate(flow.id, { archived: true });
      refresh();
    },
    [refresh]
  );
  const toggleEnabled = useCallback(
    async (flow) => {
      await api.bssQaFlowUpdate(flow.id, { enabled: !flow.enabled });
      refresh();
    },
    [refresh]
  );

  return (
    <Shell>
      <div className="bss-page-hd">
        <h1>Release flows</h1>
        <div className="bss-page-hd-right">
          <RefreshButton onClick={refresh} loading={loading} />
          <button
            type="button"
            className="bss-btn"
            onClick={() => setEditing({})}
          >
            <Icon.Plus size={14} /> New flow
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            onClick={() => setRunOpen(true)}
          >
            <Icon.Rocket size={14} /> Run release test
          </button>
        </div>
      </div>

      {loading && !data && <LoadingState />}
      {error && !data && (
        <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />
      )}

      {data && (
        <div className="bss-filter-tabs qa-flows-tabs">
          <button
            type="button"
            className={`bss-filter-tab ${tab === "published" ? "is-active" : ""}`.trim()}
            onClick={() => setTab("published")}
          >
            Published <span className="bss-count">{published.length}</span>
          </button>
          <button
            type="button"
            className={`bss-filter-tab ${tab === "pending" ? "is-active" : ""}`.trim()}
            onClick={() => setTab("pending")}
          >
            Pending <span className="bss-count">{pending.length}</span>
          </button>
        </div>
      )}

      {data && tab === "pending" && (
        <div className="qa-pending">
          {pending.length === 0 ? (
            <EmptyState
              title="No pending imports"
              message="Add cases to release flows from a ticket's QA workspace — they land here to assign an app and publish."
            />
          ) : (
            pending.map((f) => (
              <PendingFlowRow
                key={f.id}
                flow={f}
                apps={apps}
                sideEffectTags={sideEffectTags}
                tagSuggestions={tagSuggestions}
                onPublished={refresh}
                onDiscarded={refresh}
              />
            ))
          )}
        </div>
      )}

      {data && tab === "published" && (
        <div className="qa-flows">
          {published.length === 0 && (
            <EmptyState
              title="No flows yet"
              message="Author smoke/regression flows here — they run against deployed environments."
            />
          )}
          {apps.map((app) => {
            const appFlows = flowsByApp.get(app) || [];
            const login = loginByApp[app];
            return (
              <div key={app} className="qa-flows-app">
                <div className="qa-flows-app-hd">
                  <span className="qa-flows-app-name">
                    {APP_LABEL[app] || app}
                  </span>
                  <button
                    type="button"
                    className="qa-link-btn"
                    onClick={() => setLoginApp(app)}
                  >
                    <Icon.Settings size={12} />{" "}
                    {login ? "Login config" : "Add login config"}
                  </button>
                </div>
                {appFlows.length === 0 ? (
                  <p className="qa-flows-empty">No flows for this app.</p>
                ) : (
                  <table className="qa-flows-table">
                    <tbody>
                      {appFlows.map((f) => (
                        <tr
                          key={f.id}
                          className={f.enabled ? "" : "is-disabled"}
                        >
                          <td className="qa-flows-name">{f.name}</td>
                          <td className="qa-flows-tags">
                            {(f.tags || []).map((t) => (
                              <span key={t} className="qa-tag">
                                {t}
                              </span>
                            ))}
                            {(f.sideEffects || []).map((t) => (
                              <span key={t} className="qa-tag qa-tag-warn">
                                {t}
                              </span>
                            ))}
                          </td>
                          <td className="qa-flows-meta">
                            {relativeTime(f.updatedAt)}
                          </td>
                          <td className="qa-flows-row-actions">
                            <button
                              type="button"
                              className="qa-link-btn"
                              onClick={() => toggleEnabled(f)}
                            >
                              {f.enabled ? "Disable" : "Enable"}
                            </button>
                            <button
                              type="button"
                              className="qa-link-btn"
                              onClick={() => setEditing(f)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="qa-link-btn qa-link-danger"
                              onClick={() => archive(f)}
                            >
                              Archive
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      <FlowEditor
        open={!!editing}
        flow={editing && editing.id ? editing : null}
        apps={apps}
        sideEffectTags={sideEffectTags}
        tagSuggestions={tagSuggestions}
        onClose={() => setEditing(null)}
        onSaved={refresh}
      />
      <LoginEditor
        open={!!loginApp}
        app={loginApp}
        login={loginApp ? loginByApp[loginApp] : null}
        onClose={() => setLoginApp(null)}
        onSaved={refresh}
      />
      <ReleaseTestModal
        open={runOpen}
        onClose={() => setRunOpen(false)}
        flows={published}
        meta={meta}
      />
    </Shell>
  );
}
